import { motion } from "framer-motion";
import { MapPin, Phone, Navigation, Clock } from "lucide-react";
import { SITE } from "@/lib/site";

const isOpenNow = () => {
  const now = new Date();
  const day = now.getDay();
  const mins = now.getHours() * 60 + now.getMinutes();
  const open = 11 * 60;
  const close = day === 0 ? 21 * 60 : day >= 5 ? 22 * 60 + 30 : 21 * 60 + 30;
  return mins >= open && mins < close;
};

export default function LocationSection() {
  const open = isOpenNow();
  return (
    <section id="visit" className="relative py-28 sm:py-36" data-testid="location-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="font-mono-d text-[11px] uppercase tracking-[0.3em] text-terracotta mb-6" data-testid="location-overline">
              Find Us
            </p>
            <h2 className="font-serif-d text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-cream leading-[1.05] mb-10" data-testid="location-title">
              Downtown Aurora, <span className="italic text-terracotta">by the river</span>
            </h2>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-terracotta mt-1 shrink-0" />
                <div>
                  <p className="text-cream font-medium" data-testid="location-address">{SITE.address}</p>
                  <p className="text-cream/50 text-sm font-light mt-1">Plus code: 334X+FW Aurora, Indiana</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="w-5 h-5 text-terracotta mt-1 shrink-0" />
                <a href={SITE.phoneHref} className="text-cream font-medium hover:text-terracotta transition-colors" data-testid="location-phone">
                  {SITE.phone}
                </a>
              </div>
              <div className="flex items-start gap-4">
                <Clock className="w-5 h-5 text-terracotta mt-1 shrink-0" />
                <span
                  className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono-d uppercase tracking-[0.15em] ${
                    open ? "bg-agave/30 text-emerald-300" : "bg-terracotta/15 text-terracotta"
                  }`}
                  data-testid="location-open-status"
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${open ? "bg-emerald-400" : "bg-terracotta"}`} />
                  {open ? "Open now" : "Closed — opens 11:00 AM"}
                </span>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href={SITE.directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-8 py-4 rounded-full bg-terracotta text-obsidian font-semibold text-sm hover:bg-terracotta-hover transition-colors duration-300"
                data-testid="location-directions-btn"
              >
                <Navigation className="w-4 h-4" /> Get Directions
              </a>
              <a
                href={SITE.phoneHref}
                className="flex items-center gap-2 px-8 py-4 rounded-full border border-cream/25 text-cream font-medium text-sm hover:border-terracotta hover:text-terracotta transition-colors duration-300"
                data-testid="location-call-btn"
              >
                <Phone className="w-4 h-4" /> Call the Restaurant
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-6"
          >
            <div className="bg-clay-800/60 border border-terracotta/15 rounded-3xl p-8 sm:p-10" data-testid="hours-card">
              <h3 className="font-serif-d text-2xl font-semibold text-cream mb-6">Hours</h3>
              <div className="space-y-4">
                {SITE.hours.map((h) => (
                  <div key={h.days} className="flex items-baseline justify-between gap-4 border-b border-terracotta/10 pb-4 last:border-0 last:pb-0">
                    <span className="text-cream/70 text-sm font-light">{h.days}</span>
                    <span className="font-mono-d text-sm text-amber-warm">{h.time}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-xs text-cream/40 font-light">
                Dine-in · Takeout · Delivery — kitchen closes 30 minutes before doors.
              </p>
            </div>
            <div className="relative overflow-hidden rounded-3xl clipped-frame h-56 group">
              <img
                src="https://images.unsplash.com/photo-1650490632010-e18cbb694dd1?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                alt="Warm booth seating inside Alejandra's"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-obsidian/20" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
